import { useQuery } from "@tanstack/react-query";
import { VisuallyHidden } from "radix-ui";
import { useParams, useSearchParams } from "react-router-dom";
import { t } from "ttag";

import {
  getDocumentFromProviderQuery,
  getDocumentQuery,
} from "@/app/api/legacy-api";
import { LogoLoader } from "@/shared/components";
import {
  HEALTHIE_FILE_STALE_TIME,
  IMAGE_FILE_TYPES,
} from "@/shared/models/legacy-constants";
import type { Folder, ModalProps } from "@/shared/models/legacy-types";
import { Dialog, Skeleton } from "@/shared/ui";
import { isProviderFile } from "@/shared/utils";
import { ErrorState } from "./components";

export const PreviewDocument = ({ show, onClose }: ModalProps) => {
  const { documentId, folder } = useParams<{
    documentId: string;
    folder: Folder;
  }>();
  const [searchParams] = useSearchParams();
  const vendor = searchParams.get("vendor");

  const { data, isLoading, isRefetching, refetch, isError } = useQuery({
    ...(folder && isProviderFile(folder)
      ? getDocumentFromProviderQuery({ documentId, providerFolder: folder })
      : getDocumentQuery(documentId, vendor)),
    staleTime: HEALTHIE_FILE_STALE_TIME,
    refetchOnWindowFocus: false,
  });

  const documentType =
    data?.url && new URL(data?.url).searchParams.get("response-content-type");

  const isImage = Boolean(
    documentType &&
      (IMAGE_FILE_TYPES.PNG === documentType ||
        IMAGE_FILE_TYPES.JPEG === documentType),
  );

  const renderDocument = () => {
    return (
      data &&
      (isImage ? (
        <div className="flex h-0 w-full grow items-center justify-center rounded-lg bg-white">
          <img
            src={data.url}
            alt={data.name}
            className="h-full max-h-full w-full max-w-full object-contain"
          />
        </div>
      ) : (
        <div className="grow rounded-lg bg-white">
          {documentType && (
            <iframe
              title="test"
              src={data.url}
              itemType={documentType}
              className="size-full"
            />
          )}
        </div>
      ))
    );
  };

  if (!documentId || !folder) {
    return null;
  }

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <Dialog.Content>
        <VisuallyHidden.Root asChild>
          <Dialog.Description>
            {t`This modal provides a preview of the selected document, allowing you to view its content without downloading`}
          </Dialog.Description>
        </VisuallyHidden.Root>

        {isLoading && (
          <Skeleton className="h-10 w-56 self-center py-1 md:self-start" />
        )}

        {isError && <ErrorState folder={folder} onTryAgain={refetch} />}

        {data ? (
          <Dialog.Title>{data.name}</Dialog.Title>
        ) : (
          <VisuallyHidden.Root asChild>
            <Dialog.Title>{t`Preview Document`}</Dialog.Title>
          </VisuallyHidden.Root>
        )}

        {(isRefetching || isLoading) && (
          <div className="flex grow items-center justify-center rounded-lg bg-secondary-50">
            <LogoLoader />
          </div>
        )}

        {data && !isRefetching && (
          <div className="flex grow flex-col gap-2">
            {renderDocument()}
            <p className="w-fit text-sm text-secondary-950 md:mt-0 md:self-end md:text-right md:text-base">
              {t`Can't view the file correctly? Open it `}
              <a
                rel="noreferrer noopener"
                target="_blank"
                href={data.url}
                onClick={async (e) => {
                  e.preventDefault();
                  const updatedUrl = await refetch();
                  if (updatedUrl.data?.url) {
                    window.location.replace(updatedUrl.data.url);
                  }
                }}
                className="text-primary-900 underline underline-offset-2"
              >
                {t`here`}.
              </a>
            </p>
          </div>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};
